import React, { useState } from 'react';
import { Table, TableHead, TableBody, MenuItem } from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'use-toast-mui';
import QueryString from 'qs';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

import { getSmsOrderList, confirmSmsOrder, rejectSmsOrder } from '../../../api/sms';
import {
  TableControlsRow,
  PageSizeGroup,
  PageSizeLabel,
  PageSizeSelect,
  SearchGroup,
  SearchInput,
  SearchButton,
  HeaderCell,
  BodyCell,
  DataRow,
  StatusText,
  ApproveButton,
  RejectButton,
  ActionGroup,
  ApprovedText,
  PaginationContainer,
  PageButton,
  NavButton,
} from './SmsAplicationChargeHistoryStyled';

const COLUMNS = [
  { id: 'branchName', label: '센터명', width: 160 },
  { id: 'createdAt', label: '결제일시', width: 160 },
  { id: 'paymentMethod', label: '결제수단', width: 100 },
  { id: 'depositorName', label: '입금자명', width: 120 },
  { id: 'depositBank', label: '입금은행', width: 100 },
  { id: 'taxInvoiceYn', label: '세금계산서 여부', width: 130 },
  { id: 'receiptCategory', label: '발행구분', width: 120 },
  { id: 'businessNumber', label: '발행번호', width: null },
  { id: 'tokenCount', label: '충전건수', width: 100 },
  { id: 'orderStatus', label: '충전상태', width: 100 },
  { id: 'approve', label: '충전승인', width: 100 },
];

const STATUS_MAP = {
  PENDING_PAYMENT: '대기',
  PAID: '충전',
  CHARGED: '충전',
  CANCELLED: '취소',
  CANCELED: '취소',
};

const PAYMENT_METHOD_MAP = {
  BANK_TRANSFER: '계좌이체',
};

const SmsChargeHistory = () => {
  const { t } = useTranslation();
  const { user } = useSelector(state => state.user);
  const toast = useToast();
  const queryClient = useQueryClient();
  const branchId = user?.branch?.branchId;

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const query = QueryString.stringify({
    page: page - 1,
    size: pageSize,
    ...(searchKeyword && { status: searchKeyword }),
  });

  const { data: orderData } = useQuery({
    queryKey: ['smsOrders', page, pageSize, searchKeyword],
    queryFn: () => getSmsOrderList(query, user?.token),
    enabled: !!user?.token,
  });

  const orders = orderData?.content || [];
  const totalPages = orderData?.totalPages || 1;

  const confirmMutation = useMutation({
    mutationFn: orderId => confirmSmsOrder(orderId, { memo: '' }, user?.token),
    onSuccess: () => {
      toast.success(t('승인이 완료되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['smsOrders'] });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: orderId => rejectSmsOrder(orderId, user?.token),
    onSuccess: () => {
      toast.success(t('거절되었습니다'));
      queryClient.invalidateQueries({ queryKey: ['smsOrders'] });
    },
  });

  const handleReject = order => {
    if (window.confirm(t('해당 충전 신청을 거절하시겠습니까?'))) {
      rejectMutation.mutate(order.id);
    }
  };

  const handleSearch = () => {
    setSearchKeyword(searchValue);
    setPage(1);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter') handleSearch();
  };

  const renderCellValue = (order, columnId) => {
    switch (columnId) {
      case 'branchName':
        return order.branchName || user?.branch?.branchName || '-';
      case 'createdAt':
        return order.registeredDate || '-';
      case 'paymentMethod':
        return PAYMENT_METHOD_MAP[order.paymentMethod] || order.paymentMethod || '-';
      case 'depositorName':
        return order.depositorName || '-';
      case 'depositBank':
        return order.depositBank || '-';
      case 'taxInvoiceYn':
        return order.taxInvoiceYn === 'Y' ? '발행' : '미발행';
      case 'receiptCategory':
        return order.taxInvoiceYn === 'Y' ? order.receiptCategory || '사업자 지출증빙' : '-';
      case 'businessNumber':
        return order.taxInvoiceYn === 'Y' ? order.businessNumber || '-' : '-';
      case 'tokenCount':
        return order.tokenCount ? `${order.tokenCount.toLocaleString()} 건` : '-';
      case 'orderStatus':
        return (
          <StatusText status={order.orderStatus}>
            {STATUS_MAP[order.orderStatus] || order.orderStatus || '-'}
          </StatusText>
        );
      case 'approve':
        if (order.orderStatus === 'PENDING_PAYMENT') {
          return (
            <ActionGroup>
              <ApproveButton onClick={() => confirmMutation.mutate(order.id)} disabled={confirmMutation.isPending}>
                {t('승인')}
              </ApproveButton>
              <RejectButton onClick={() => handleReject(order)} disabled={rejectMutation.isPending}>
                {t('거절')}
              </RejectButton>
            </ActionGroup>
          );
        }
        if (order.orderStatus === 'PAID' || order.orderStatus === 'CHARGED') {
          return <ApprovedText>{t('승인완료')}</ApprovedText>;
        }
        if (order.orderStatus === 'CANCELLED' || order.orderStatus === 'CANCELED') {
          return <ApprovedText>{t('취소')}</ApprovedText>;
        }
        return <ApprovedText>{t(STATUS_MAP[order.orderStatus] || '-')}</ApprovedText>;
      default:
        return '-';
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return (
      <PaginationContainer>
        <NavButton onClick={() => setPage(prev => Math.max(1, prev - 1))} disabled={page === 1}>
          <KeyboardArrowLeftIcon />
        </NavButton>
        {pages.map(p => (
          <PageButton key={p} isActive={p === page} onClick={() => setPage(p)}>
            {p}
          </PageButton>
        ))}
        {endPage < totalPages && (
          <>
            <PageButton disabled>...</PageButton>
            <PageButton onClick={() => setPage(totalPages)}>{totalPages}</PageButton>
          </>
        )}
        <NavButton onClick={() => setPage(prev => Math.min(totalPages, prev + 1))} disabled={page === totalPages}>
          <KeyboardArrowRightIcon />
        </NavButton>
      </PaginationContainer>
    );
  };

  return (
    <>
      <TableControlsRow>
        <PageSizeGroup>
          <PageSizeLabel>{t('페이지당 항목 수')}</PageSizeLabel>
          <PageSizeSelect
            value={pageSize}
            onChange={e => {
              setPageSize(e.target.value);
              setPage(1);
            }}>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={20}>20</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </PageSizeSelect>
        </PageSizeGroup>
        <SearchGroup>
          <SearchInput
            placeholder={t('검색어를 입력해 주세요')}
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <SearchButton onClick={handleSearch}>{t('검색')}</SearchButton>
        </SearchGroup>
      </TableControlsRow>

      <Table>
        <TableHead>
          <DataRow>
            {COLUMNS.map(col => (
              <HeaderCell key={col.id} style={{ width: col.width || 'auto' }}>
                {t(col.label)}
              </HeaderCell>
            ))}
          </DataRow>
        </TableHead>
        <TableBody>
          {orders.length > 0 ? (
            orders.map((order, idx) => (
              <DataRow key={order.id || idx}>
                {COLUMNS.map(col => (
                  <BodyCell key={col.id} style={{ width: col.width || 'auto' }}>
                    {renderCellValue(order, col.id)}
                  </BodyCell>
                ))}
              </DataRow>
            ))
          ) : (
            <DataRow>
              <BodyCell colSpan={COLUMNS.length} style={{ textAlign: 'center', color: '#a6a6a6' }}>
                {t('데이터가 없습니다')}
              </BodyCell>
            </DataRow>
          )}
        </TableBody>
      </Table>

      {renderPagination()}
    </>
  );
};

export default SmsChargeHistory;
